import { Component, inject, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { CommonModule, DatePipe } from '@angular/common';
import { IDiscountCoupon, IDiscountCouponRequest } from '@app/shared/interfaces/discount-coupon.interface';
import { DialogModule } from 'primeng/dialog';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DiscountCouponService } from '@services/discount-coupon.service';
import { MessageService } from 'primeng/api';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'p-discount-coupon',
    imports: [
        ButtonModule,
        InputText,
        FormsModule,
        CommonModule,
        ReactiveFormsModule,
        DialogModule,
        TableModule,
        ToggleSwitch,
        InputNumber,
        DatePicker,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './discount-coupon.component.html',
    styleUrl: './discount-coupon.component.scss',
    providers: [DatePipe]
})
export class DiscountCouponComponent implements OnInit {
    coupons: IDiscountCoupon[] = [];
    @ViewChild('dt') dt!: Table;
    dialogVisible: boolean = false;
    selectedCoupon: IDiscountCoupon | null = null;
    isEditMode: boolean = false;
    private fb = inject(FormBuilder);
    couponForm: FormGroup = this.fb.group({
        name: ['', [Validators.required, Validators.maxLength(255)]],
        code: ['', [Validators.required, Validators.maxLength(10)]],
        amount: [0, [Validators.required, Validators.min(0)]],
        active: [true],
        startDate: [null],
        endDate: [null]
    });
    submitted: boolean = false;
    loading = false;

    cols = [
        { field: 'name', header: 'Name' },
        { field: 'code', header: 'Code' },
        { field: 'amount', header: 'Amount' },
        { field: 'active', header: 'Status' },
        { field: 'validity', header: 'Validity' },
        { field: 'startDate', header: 'Start Date (UTC)' },
        { field: 'endDate', header: 'End Date (UTC)' },
        { field: 'created', header: 'Created' }
    ];

    private discountCouponService = inject(DiscountCouponService);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.loadCoupons();
    }

    onGlobalSearch(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadCoupons() {
        this.loading = true;
        this.cd.detectChanges();
        this.discountCouponService.getAll().subscribe({
            next: (data) => {
                this.coupons = data;
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    isInvalid(field: string): boolean {
        const control = this.couponForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    toUpperCase(field: string): void {
        const control = this.couponForm.get(field);
        if (control && control.value) {
            control.setValue(control.value.toUpperCase(), { emitEvent: false });
        }
    }

    openCreate() {
        this.dialogVisible = true;
        this.resetForm();
        this.isEditMode = false;
        this.selectedCoupon = null;
        this.submitted = false;
    }

    openEdit(coupon: IDiscountCoupon) {
        this.isEditMode = true;
        this.selectedCoupon = coupon;
        this.submitted = false;
        this.populateForm(coupon);
        this.dialogVisible = true;
    }

    populateForm(coupon: IDiscountCoupon): void {
        this.couponForm.patchValue({
            name: coupon.name,
            code: coupon.code,
            amount: coupon.amount,
            active: coupon.active,
            startDate: coupon.startDate ? new Date(coupon.startDate) : null,
            endDate: coupon.endDate ? new Date(coupon.endDate) : null
        });
    }

    cancelDialog() {
        this.dialogVisible = false;
        this.resetForm();
    }

    saveCoupon() {
        this.submitted = true;

        if (this.couponForm.invalid) {
            return;
        }

        const formData = this.couponForm.value;

        // Validate endDate is not before startDate
        if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'End date cannot be before start date.'
            });
            return;
        }

        const request: IDiscountCouponRequest = {
            name: formData.name,
            code: formData.code,
            amount: formData.amount,
            active: formData.active,
            startDate: formData.startDate ? this.formatDate(formData.startDate) : '',
            endDate: formData.endDate ? this.formatDate(formData.endDate) : ''
        };

        if (!this.isEditMode) {
            this.discountCouponService.create(request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Coupon has been created successfully.'
                    });
                    this.loadCoupons();
                    this.dialogVisible = false;
                    this.resetForm();
                }
            });
        } else if (this.selectedCoupon) {
            this.discountCouponService.update(this.selectedCoupon.id, request).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Coupon has been updated successfully.'
                    });
                    this.loadCoupons();
                    this.dialogVisible = false;
                    this.resetForm();
                }
            });
        }
    }

    resetForm() {
        this.couponForm.reset({
            name: '',
            code: '',
            amount: 0,
            active: true,
            startDate: null,
            endDate: null
        });
        this.submitted = false;
    }

    clearChanges() {
        if (this.isEditMode && this.selectedCoupon) {
            this.populateForm(this.selectedCoupon);
            this.couponForm.markAsPristine();
            this.couponForm.markAsUntouched();
            this.submitted = false;
        } else {
            this.resetForm();
        }
    }

    private formatDate(date: Date): string {
        return date.toISOString().slice(0, 19);
    }

    isExpired(endDate: string): boolean {
        if (!endDate) return false;
        const utcEndDate = endDate.endsWith('Z') ? endDate : endDate + 'Z';
        return new Date(utcEndDate) < new Date();
    }
}
